import { CardData } from "@/components/CardData";
import { HeaderPage } from "@/components/HeaderPage";
import { IoWarningOutline } from "react-icons/io5";
import { LuClock10 } from "react-icons/lu";
import { FaRegCheckCircle } from "react-icons/fa";
import { MdOutlineShowChart } from "react-icons/md";
import { CardContent } from "@/components/CardContent";
import { Table } from "@/components/Table";
import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  RefreshCw,
  Maximize2,
  Minimize2,
  Filter,
  X as XIcon,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
  Cell,
} from "recharts";
import { Calendar, Eye, MapPin, Play, Newspaper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetOcurrences } from "@/hooks/useOccurrences";
import type { OccurrenceDto } from "@/dtos/CategoriesResponseDto";
import { formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { Loading } from "@/components/Loading";
import { motion } from "framer-motion";
import { useGetCategories } from "@/hooks/useCategories";
import {
  useGetKPISData,
  useGetWeeklyOccurrences,
  useGetOccurrencesByCategory,
} from "@/hooks/useDashboard";

// Dados de fallback caso a API não retorne
const fallbackWeeklyData = [
  { name: "Seg", ocorrencias: 24, resolvidas: 18 },
  { name: "Ter", ocorrencias: 32, resolvidas: 25 },
  { name: "Qua", ocorrencias: 28, resolvidas: 22 },
  { name: "Qui", ocorrencias: 45, resolvidas: 30 },
  { name: "Sex", ocorrencias: 38, resolvidas: 35 },
  { name: "Sáb", ocorrencias: 15, resolvidas: 12 },
  { name: "Dom", ocorrencias: 8, resolvidas: 6 },
];

const fallbackCategoryData = [
  { name: "Iluminação", value: 35, color: "hsl(var(--chart-1))" },
  { name: "Buracos", value: 28, color: "hsl(var(--chart-2))" },
  { name: "Limpeza", value: 18, color: "hsl(var(--chart-3))" },
  { name: "Trânsito", value: 12, color: "hsl(var(--chart-4))" },
  { name: "Outros", value: 7, color: "hsl(var(--chart-5))" },
];

const statusConfig: Record<
  string,
  {
    label: string;
    variant:
      | "default"
      | "secondary"
      | "destructive"
      | "outline"
      | "warning"
      | "alert";
  }
> = {
  AWAITING_ANALYSIS: { label: "Aguardando Análise", variant: "outline" },
  IN_PROGRESS: { label: "Em Andamento", variant: "secondary" },
  RESOLVED: { label: "Resolvida", variant: "default" },
  // REJECTED: { label: "Rejeitada", variant: "destructive" },
  CANCELLED: { label: "Cancelada", variant: "destructive" },
  REOPENED: { label: "Reaberta", variant: "outline" },
  RETURNED: { label: "Devolvida", variant: "warning" },
  AWAITING_REANALYSIS: { label: "Aguardando Reanálise", variant: "secondary" },
  FILED: { label: "Arquivada", variant: "alert" },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  LOW: {
    label: "Baixa",
    color: "bg-green-100 text-green-700",
  },
  MEDIUM: { label: "Média", color: "bg-yellow-100 text-yellow-700" },
  HIGH: { label: "Alta", color: "bg-orange-100 text-orange-700" },
  URGENT: {
    label: "Crítica",
    color: "bg-red-600 text-white",
  },
};

// Função para obter o primeiro dia da semana (segunda-feira)
const getFirstDayOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Se for domingo (0), volta 6 dias
  const firstDay = new Date(now);
  firstDay.setDate(now.getDate() + diff);
  return firstDay.toISOString().split("T")[0];
};

// Função para obter o último dia da semana (domingo)
const getLastDayOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek; // Se for domingo, não precisa ajustar
  const lastDay = new Date(now);
  lastDay.setDate(now.getDate() + diff);
  return lastDay.toISOString().split("T")[0];
};

export const Home = () => {
  const navigate = useNavigate();
  const [autoRefreshInterval, setAutoRefreshInterval] =
    useState<number>(120000); // 2 minutos padrão
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [, setTick] = useState(0); // Força re-render para atualizar o contador
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Estados para filtros - valores padrão: primeira e última dia da semana atual
  const [startDate, setStartDate] = useState<string>(getFirstDayOfWeek());
  const [endDate, setEndDate] = useState<string>(getLastDayOfWeek());
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  // Buscar categorias para o filtro
  const { data: categoriesData } = useGetCategories();
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.data || categoriesData?.categories || [];

  // Preparar filtros para os hooks
  const dashboardFilters = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    categoryId: selectedCategoryId || undefined,
  };

  const {
    data: occurrencesData,
    isLoading,
    refetch: refetchOccurrences,
  } = useGetOcurrences({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
    startDate: dashboardFilters.startDate,
    endDate: dashboardFilters.endDate,
    categoryId: dashboardFilters.categoryId,
  });

  const {
    data: kpisData,
    isLoading: loadingKPIs,
    refetch: refetchKPIs,
  } = useGetKPISData(dashboardFilters);
  const {
    data: weeklyData,
    isLoading: loadingWeekly,
    refetch: refetchWeekly,
  } = useGetWeeklyOccurrences(dashboardFilters);
  const {
    data: categoryData,
    isLoading: loadingCategory,
    refetch: refetchCategory,
  } = useGetOccurrencesByCategory({
    startDate: dashboardFilters.startDate,
    endDate: dashboardFilters.endDate,
  });

  // Atualizar o contador a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-refresh dos dados
  useEffect(() => {
    if (autoRefreshInterval === 0) return; // Desabilitado

    const interval = setInterval(() => {
      refetchKPIs();
      refetchWeekly();
      refetchCategory();
      refetchOccurrences();
      setLastUpdate(new Date());
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [
    autoRefreshInterval,
    refetchKPIs,
    refetchWeekly,
    refetchCategory,
    refetchOccurrences,
  ]);

  // Gerenciar modo fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const clearFilters = () => {
    setStartDate(getFirstDayOfWeek());
    setEndDate(getLastDayOfWeek());
    setSelectedCategoryId("");
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

    if (diff < 60) return `Há ${diff}s`;
    if (diff < 3600) return `Há ${Math.floor(diff / 60)}min`;
    return lastUpdate.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className={
        isFullscreen ? "fixed inset-0 z-[9999] bg-gray-50 overflow-auto" : ""
      }
    >
      {isFullscreen && (
        <div className="sticky top-0 z-[10000] bg-white border-b shadow-sm p-4 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4" />
              <span>{formatLastUpdate()}</span>
            </div>
            <Select
              value={autoRefreshInterval.toString()}
              onValueChange={(value) => {
                setAutoRefreshInterval(Number(value));
                setLastUpdate(new Date());
              }}
            >
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="Intervalo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Desabilitado</SelectItem>
                <SelectItem value="30000">30 segundos</SelectItem>
                <SelectItem value="60000">1 minuto</SelectItem>
                <SelectItem value="120000">2 minutos</SelectItem>
                <SelectItem value="300000">5 minutos</SelectItem>
                <SelectItem value="600000">10 minutos</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="gap-2"
            >
              <Minimize2 className="h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      )}

      {!isFullscreen && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <HeaderPage
              title="Dashboard"
              description="Visão geral das ocorrências e atividades do sistema"
            />
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4" />
                <span>{formatLastUpdate()}</span>
              </div>
              <Select
                value={autoRefreshInterval.toString()}
                onValueChange={(value) => {
                  setAutoRefreshInterval(Number(value));
                  setLastUpdate(new Date());
                }}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Intervalo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Desabilitado</SelectItem>
                  <SelectItem value="30000">30 segundos</SelectItem>
                  <SelectItem value="60000">1 minuto</SelectItem>
                  <SelectItem value="120000">2 minutos</SelectItem>
                  <SelectItem value="300000">5 minutos</SelectItem>
                  <SelectItem value="600000">10 minutos</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
                className="gap-2"
              >
                <Maximize2 className="h-4 w-4" />
                Tela Cheia
              </Button>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Filter className="h-4 w-4" />
                <span>Filtros</span>
              </div>
              <div className="flex-1 flex items-center gap-3">
                <div className="flex-1 max-w-xs">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Data inicial"
                    className="h-9"
                  />
                </div>
                <div className="flex-1 max-w-xs">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="Data final"
                    className="h-9"
                  />
                </div>
                <div className="flex-1 max-w-xs">
                  <Select
                    value={selectedCategoryId || "all"}
                    onValueChange={(value) =>
                      setSelectedCategoryId(value === "all" ? "" : value)
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todas as categorias" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as categorias</SelectItem>
                      {categories.map((category: any) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(startDate !== getFirstDayOfWeek() ||
                  endDate !== getLastDayOfWeek() ||
                  selectedCategoryId) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="gap-2 h-9"
                  >
                    <XIcon className="h-4 w-4" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={
          isFullscreen
            ? "p-8 mt-6 flex flex-col gap-3"
            : "mt-6 flex flex-col gap-3"
        }
      >
        <motion.div
          className="flex justify-between gap-3"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="flex-1"
          >
            <CardData
              title="Ocorrências Abertas"
              value={loadingKPIs ? "..." : kpisData?.ocorrenciasAbertas || 0}
              iconElement={
                <div className="bg-orange-500/10 px-2.5 py-2.5 rounded-xl">
                  <IoWarningOutline size={24} className="text-orange-500" />
                </div>
              }
            />
          </motion.div>
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="flex-1"
          >
            <CardData
              title="Em Atendimento"
              value={loadingKPIs ? "..." : kpisData?.emAtendimento || 0}
              iconElement={
                <div className="bg-blue-500/10 px-2.5 py-2.5 rounded-xl">
                  <LuClock10 size={24} className="text-blue-500" />
                </div>
              }
            />
          </motion.div>
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="flex-1"
          >
            <CardData
              title="Resolvidas (Mês)"
              value={loadingKPIs ? "..." : kpisData?.resolvidas || 0}
              iconElement={
                <div className="bg-green-600/10 px-2.5 py-2.5 rounded-xl">
                  <FaRegCheckCircle size={24} className="text-green-600" />
                </div>
              }
            />
          </motion.div>
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="flex-1"
          >
            <CardData
              title="Tempo Médio"
              value={
                loadingKPIs
                  ? "..."
                  : `${kpisData?.tempoMedioResolucaoHoras || 0}h`
              }
              iconElement={
                <div className="bg-green-600/10 px-2.5 py-2.5 rounded-xl">
                  <MdOutlineShowChart size={24} className="text-green-600" />
                </div>
              }
            />
          </motion.div>
        </motion.div>
        <motion.div
          className="flex w-full gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="flex-1">
            <CardContent
              title="Ocorrências da Semana"
              content={
                loadingWeekly ? (
                  <div className="flex items-center justify-center h-[280px]">
                    <Loading label="Carregando dados..." />
                  </div>
                ) : (
                  <div className="flex-1 w-full">
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart
                        data={weeklyData || fallbackWeeklyData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id="colorOcorrencias"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="hsl(var(--chart-2))"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="hsl(var(--chart-2))"
                              stopOpacity={0}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorResolvidas"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="hsl(var(--chart-1))"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="hsl(var(--chart-1))"
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="hsl(var(--border))"
                        />
                        <XAxis
                          dataKey="name"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="ocorrencias"
                          stroke="hsl(var(--chart-2))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorOcorrencias)"
                          name="Novas Ocorrências"
                        />
                        <Area
                          type="monotone"
                          dataKey="resolvidas"
                          stroke="hsl(var(--chart-1))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorResolvidas)"
                          name="Resolvidas"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )
              }
            />
          </div>
          <div className="w-1/2 h-full">
            <CardContent
              title="Por Categoria"
              content={
                loadingCategory ? (
                  <div className="flex items-center justify-center h-[280px]">
                    <Loading label="Carregando dados..." />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={categoryData || fallbackCategoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {(categoryData || fallbackCategoryData).map(
                          (entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ),
                        )}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: number | string | undefined) => [
                          `${value ?? 0}%`,
                          "Porcentagem",
                        ]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span className="text-sm text-foreground">
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )
              }
            />
          </div>
        </motion.div>

        <div className="flex w-full"></div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <CardContent
            title="Ocorrências Recentes"
            content={
              isLoading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <Loading label="Carregando ocorrências..." />
                </div>
              ) : (
                <Table<OccurrenceDto>
                  columns={[
                    { key: "protocol", label: "Protocolo" },
                    {
                      key: "category",
                      label: "Categoria",
                      render: (item) => (
                        <div>
                          <p className="font-medium text-sm">
                            {item.category.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.subcategory.name}
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: "description",
                      label: "Descrição",
                      render: (item) => (
                        <p className="truncate max-w-[250px] text-sm text-gray-600">
                          {item.description}
                        </p>
                      ),
                    },
                    {
                      key: "neighborhood",
                      label: "Local",
                      render: (item) => (
                        <div className="flex items-start gap-1">
                          <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 truncate max-w-[150px]">
                              {item.address}
                            </p>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "citizen",
                      label: "Cidadão",
                      render: (item) => (
                        <span className="text-sm text-gray-600">
                          {item.user?.profile?.fullName}
                        </span>
                      ),
                    },
                    {
                      key: "priority",
                      label: "Prioridade",
                      render: (item) => {
                        const label =
                          item.status === "IN_PROGRESS"
                            ? item.priority
                            : item.userPerceivedUrgency;
                        const config = priorityConfig[
                          item.status === "IN_PROGRESS"
                            ? item.priority
                            : item.userPerceivedUrgency
                        ] || {
                          label: label,
                          color: "bg-gray-100 text-gray-700",
                        };
                        return (
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}
                          >
                            {config.label}
                          </span>
                        );
                      },
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (item) => {
                        const config = statusConfig[item.status] || {
                          label: item.status,
                          variant: "outline" as const,
                        };
                        return (
                          <Badge variant={config.variant}>{config.label}</Badge>
                        );
                      },
                    },
                    {
                      key: "createdAt",
                      label: "Data",
                      render: (item) => (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(item.createdAt)}
                        </div>
                      ),
                    },
                    {
                      key: "actions",
                      label: "Ações",
                      render: (item) => (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() =>
                              navigate(`/occurrences/${item.protocol}`)
                            }
                            title="Visualizar"
                          >
                            <Eye className="h-5 w-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 transition-colors ${
                              item.status === "AWAITING_ANALYSIS"
                                ? "cursor-pointer hover:bg-green-50 text-green-600"
                                : "cursor-not-allowed text-gray-400"
                            }`}
                            onClick={() => {
                              if (item.status === "AWAITING_ANALYSIS") {
                                navigate(
                                  `/occurrences/${item.protocol}/screening`,
                                );
                              }
                            }}
                            disabled={item.status !== "AWAITING_ANALYSIS"}
                            title={
                              item.status === "AWAITING_ANALYSIS"
                                ? "Triagem"
                                : "Triagem já realizada"
                            }
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 transition-colors ${
                              item.status === "IN_PROGRESS"
                                ? "cursor-pointer "
                                : "cursor-not-allowed text-gray-400"
                            }`}
                            onClick={() => {
                              if (item.status === "IN_PROGRESS") {
                                navigate("/service-orders/create", {
                                  state: {
                                    fromOccurrence: true,
                                    occurrence: item,
                                  },
                                });
                              }
                            }}
                            disabled={item.status !== "IN_PROGRESS"}
                            title={
                              item.status === "IN_PROGRESS"
                                ? "Criar Ordem de Serviço"
                                : "Disponível apenas para ocorrências em andamento"
                            }
                          >
                            <Newspaper className="h-4 w-4" />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                  data={occurrencesData?.data || []}
                />
              )
            }
          />
        </motion.div>
      </div>
    </div>
  );
};
